<template>
  <div class="attachments-list">
    <div
      v-for="attachment in attachments"
      :key="attachment.id"
      :class="['attachment', { error: !attachment.category }]"
    >
      <div v-if="!attachment.category" class="mask">
        {{ $t('attachments.errors.categoryNotSelected') }}
      </div>
      <div class="attachment-icon">
        <font-awesome-icon :icon="getFileIcon(attachment.originalName)" />
      </div>

      <div class="attachment-name">
        <span>{{ attachment.originalName }}</span>
      </div>
      <dropdown-select
        v-if="!attachment.category"
        :menu-items="attachmentOptions"
        :selected-item="attachment.category"
        class="attachment-dropdown"
        @select-item="updateAttachmentCategory(attachment.id, $event)"
      />
      <label v-else class="dropdown-label">{{ attachment.category }}</label>
      <div>
        {{
          $t('attachments.uploadedBy', {
            name: `${attachment.last_name}, ${attachment.first_name}`,
            date: formatDate(attachment.createdAt)
          })
        }}
      </div>
      <div class="attachment-actions">
        <div
          v-if="
            canDownloadAttachment.includes(
              `${attachment.category.toLowerCase().replace(/[-]+/g, ' ')}`
            ) || isSystemAdmin
          "
          class="attachment-download"
          @click.prevent="download(attachment.id, attachment.originalName)"
        >
          <font-awesome-icon icon="file-download" />
        </div>
        <div class="attachment-delete" @click="$emit('delete-attachment', attachment.id)">
          <font-awesome-icon icon="trash-alt" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { attachmentService } from './attachment.service';
import DropdownSelect from '@/app/components/DropdownSelect';
import readable from '@/filters/readable.filter';
import formatDateTime from '@/filters/format-datetime.filter.js';

export default {
  name: 'AttachmentsList',
  components: {
    DropdownSelect
  },
  props: {
    attachments: {
      type: Array,
      required: true
    },
    attachmentOptions: {
      type: Array,
      default: () => []
    },
    attachmentType: {
      type: String,
      required: true
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    isSystemAdmin() {
      return (
        this.currentUser &&
        this.currentUser.roles.map(r => r.role_name).some(role => role === 'System Admin')
      );
    },
    formatPermissionStoreWithSpecialCharacters() {
      let attachmentPermissions = [];
      let attachmentPermissionsStore = this.currentUser.permissions[this.attachmentType][
        'DOWNLOAD_ATTACHMENT'
      ];
      if (attachmentPermissionsStore) {
        attachmentPermissionsStore.forEach(perm => {
          let newPerm = perm
            .replaceAll('_', ' ')
            .toLowerCase()
            .split(' ');
          for (let i = 0; i < newPerm.length; i++) {
            newPerm[i] = newPerm[i][0].toUpperCase() + newPerm[i].substr(1);
            switch (newPerm[i]) {
              case 'And':
                newPerm[i] = '&';
                break;
              case 'Bgc':
                newPerm[i] = 'BGC';
                break;
              case 'Aoda':
                newPerm[i] = 'AODA';
                break;
            }
          }
          let finalPerm = newPerm.join(' ');
          attachmentPermissions.push(
            readable(finalPerm)
              .toLowerCase()
              .replace(/[-]+/g, ' ')
          );
        });
      }
      return attachmentPermissions;
    },
    formatPermissionStoreWithOutSpecialCharacters() {
      let attachmentPermissions = [];
      let attachmentPermissionsStore = this.currentUser.permissions[this.attachmentType][
        'DOWNLOAD_ATTACHMENT'
      ];
      if (attachmentPermissionsStore) {
        let tsAttachmentTypes = [];
        tsAttachmentTypes = attachmentPermissionsStore.map(item =>
          readable(item)
            .toLowerCase()
            .replace(/[-]+/g, ' ')
        );
        attachmentPermissions.push(...tsAttachmentTypes);
      }
      return attachmentPermissions;
    },
    canDownloadAttachment() {
      let attachmentPermissions = [];
      switch (this.attachmentType) {
        case 'ASSIGNMENT':
        case 'CONTRACTOR':
          attachmentPermissions = this.formatPermissionStoreWithSpecialCharacters;
          break;
        case 'CLIENT':
        case 'EXPENSE_REPORT':
        case 'PAYROLL_OVERVIEW':
        case 'PROJECT':
        case 'PROJECT_EVENT_TYPE':
        case 'PERM_PLACEMENT':
        case 'REPORT':
        case 'SUPPLIER':
        case 'TIME_SHEET':
        default:
          attachmentPermissions = this.formatPermissionStoreWithOutSpecialCharacters;
          break;
      }

      return attachmentPermissions;
    }
  },
  mounted() {},
  methods: {
    isInvoice(attachment) {
      return attachment.category === 'Invoice';
    },
    download(attachmentId, originalName) {
      attachmentService.download(attachmentId, originalName);
    },
    async updateAttachmentCategory(id, event) {
      await attachmentService.updateAttachmentCategory(id, event);
      this.$emit('refresh');
    },
    getFileIcon(fileName) {
      const fileExtensionPos = fileName.lastIndexOf('.');
      const ext = fileName.substr(fileExtensionPos + 1);
      switch (ext.toLowerCase()) {
        case 'doc':
        case 'docx':
          return 'file-word';
        case 'pdf':
          return 'file-pdf';
        case 'xls':
        case 'xlsx':
        case 'csv':
          return 'file-excel';
        case 'gif':
        case 'png':
          return 'file-image';
        default:
          return 'file-alt';
      }
    },
    formatDate(date) {
      return date ? formatDateTime(date) : null;
    }
  }
};
</script>
<style lang="scss" scoped>
@import '../../styles/colors.scss';
@import '../../styles/variables.scss';

.attachments-list {
  background-color: $background-level-2;
  padding: 20px 60px;

  .attachment {
    position: relative;
    margin: 10px 0px;
    padding: $attachment-padding;
    display: grid;
    grid-template-columns: 30px 0.8fr 0.8fr 1fr 60px;
    column-gap: 30px;
    align-items: center;
    &.error {
      border: 1px solid $red;

      .attachment-dropdown {
        width: unset;
      }
    }

    .mask {
      position: absolute;
      height: 100%;
      width: 30%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: $row-border;
      color: $red;
      z-index: 100;
    }
  }
  .attachment-name {
    display: inline-block;
    color: $text-color-dark;
    font-size: 16px;
    font-weight: bold;
    letter-spacing: -0.16px;
    line-height: 19px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .attachment-icon {
    display: inline-block;
    position: relative;
    color: $text-color-dark-3;
    background-color: $background-light;
    padding: 10px;
    height: 30px;
    border-radius: 50%;
    svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .dropdown-label {
    display: inline-block;
  }
  .attachment-dropdown {
    display: inline-block;
    margin-left: 20px;
    width: 30%;
  }
  .attachment-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    color: $text-color-dark-3;

    .attachment-download,
    .attachment-delete {
      position: relative;
      width: 30px;
      height: 30px;
      svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      &:hover {
        cursor: pointer;
      }
    }
  }
}
</style>
